/**
 * useConnectorExecutions - Hooks for connector execution monitoring
 *
 * Provides React Query hooks for fetching execution history, details, metrics,
 * and latest execution data for connector monitoring UI.
 */
import { useQuery } from '@tanstack/react-query';
import { apiClient } from '@/lib/api/client';
import type { ConnectorExecution, ExecutionMetrics } from '@/types/connectors';

const API_BASE = '/connectors';

/**
 * Response interface for execution history endpoint
 * Matches backend ExecutionHistoryResponse model
 */
interface ExecutionHistoryResponse {
  connector_type: string;
  executions: ConnectorExecution[];
  total: number;
  limit: number;
  offset: number;
  has_more: boolean;
}

/**
 * Fetch paginated execution history for a connector
 *
 * Backend endpoint: GET /{connector_type}/{name}/history
 * Uses limit/offset pagination (not page/page_size)
 */
async function fetchExecutionHistory(
  connectorType: string,
  name: string,
  page: number = 1,
  pageSize: number = 50
): Promise<ExecutionHistoryResponse> {
  // Convert page-based pagination to offset-based
  const offset = (page - 1) * pageSize;
  const response = await apiClient.get(`${API_BASE}/${connectorType}/${encodeURIComponent(name)}/history`, {
    params: { limit: pageSize, offset },
  });
  return response.data;
}

/**
 * Fetch details for a specific execution
 */
async function fetchExecutionDetails(
  connectorType: string,
  name: string,
  executionId: string
): Promise<ConnectorExecution> {
  const response = await apiClient.get(
    `${API_BASE}/${connectorType}/${encodeURIComponent(name)}/executions/${executionId}`
  );
  return response.data;
}

/**
 * Fetch metrics for a connector over a time period
 */
async function fetchConnectorMetrics(
  connectorType: string,
  name: string,
  periodDays: number = 30
): Promise<ExecutionMetrics> {
  const response = await apiClient.get(`${API_BASE}/${connectorType}/${encodeURIComponent(name)}/metrics`, {
    params: { period_days: periodDays },
  });
  return response.data;
}

/**
 * Fetch the most recent execution for a connector
 */
async function fetchLatestExecution(
  connectorType: string,
  name: string
): Promise<ConnectorExecution | null> {
  try {
    const response = await apiClient.get(`${API_BASE}/${connectorType}/${encodeURIComponent(name)}`);
    return response.data.latest_execution || null;
  } catch (error: any) {
    if (error?.response?.status === 404) {
      return null;
    }
    throw error;
  }
}

/**
 * Hook to fetch execution history with pagination
 *
 * Backend endpoint: GET /connectors/{connector_type}/{name}/history
 *
 * @param connectorType - The type of connector (e.g., 'google_ads')
 * @param name - The connector configuration name (required)
 * @param page - Page number (1-indexed), converted to offset internally
 * @param pageSize - Number of items per page (maps to 'limit' param)
 * @param enabled - Whether to enable the query
 *
 * @returns ExecutionHistoryResponse with:
 *   - executions: Array of execution records
 *   - total: Total count of executions
 *   - limit: Page size used
 *   - offset: Current offset
 *   - has_more: Whether more pages exist
 *
 * @example
 * const { data, isLoading } = useExecutionHistory('google_ads', 'my-config', 1, 50);
 * // Access: data.executions, data.total, data.has_more
 */
export function useExecutionHistory(
  connectorType: string,
  name: string,
  page: number = 1,
  pageSize: number = 50,
  enabled: boolean = true
) {
  return useQuery({
    queryKey: ['executionHistory', connectorType, name, page, pageSize],
    queryFn: () => fetchExecutionHistory(connectorType, name, page, pageSize),
    enabled: enabled && !!connectorType && !!name,
    // Keep the default 30s staleTime — the dynamic refetchInterval below
    // takes over while rows are active.
    staleTime: 30_000,
    refetchOnMount: 'always',
    // Dynamic poll: 3s while any row is running/pending, off otherwise.
    // Matches the detail-pane polling cadence so the list reflects status
    // transitions as quickly as the right pane does.
    refetchInterval: (query) => {
      const items = query?.state?.data?.executions ?? [];
      const active = items.some(
        (e: { status?: string }) =>
          e.status === 'running' || e.status === 'pending'
      );
      return active ? 3_000 : false;
    },
    refetchIntervalInBackground: true,
  });
}

/**
 * Hook to fetch details for a specific execution
 *
 * Backend endpoint: GET /connectors/{connector_type}/{name}/executions/{execution_id}
 *
 * @param connectorType - The type of connector
 * @param name - The connector configuration name (required)
 * @param executionId - The execution ID to fetch
 * @param enabled - Whether to enable the query
 *
 * @example
 * const { data } = useExecutionDetails('google_ads', 'my-config', 'exec-123');
 */
export function useExecutionDetails(
  connectorType: string,
  name: string,
  executionId: string,
  enabled: boolean = true
) {
  return useQuery({
    queryKey: ['executionDetails', connectorType, name, executionId],
    queryFn: () => fetchExecutionDetails(connectorType, name, executionId),
    enabled: enabled && !!connectorType && !!name && !!executionId,
    staleTime: 60000, // 1 minute - execution details don't change
  });
}

/**
 * Hook to fetch connector metrics over a time period
 *
 * Backend endpoint: GET /connectors/{connector_type}/{name}/metrics
 * Query param: period_days (integer, 1-365)
 *
 * @param connectorType - The type of connector
 * @param name - The connector configuration name (required)
 * @param periodDays - Number of days to analyze (default: 30, range: 1-365)
 * @param enabled - Whether to enable the query
 *
 * @example
 * const { data: metrics } = useConnectorMetrics('google_ads', 'my-config', 7);
 */
export function useConnectorMetrics(
  connectorType: string,
  name: string,
  periodDays: number = 30,
  enabled: boolean = true
) {
  return useQuery({
    queryKey: ['connectorMetrics', connectorType, name, periodDays],
    queryFn: () => fetchConnectorMetrics(connectorType, name, periodDays),
    enabled: enabled && !!connectorType && !!name,
    staleTime: 60000, // 1 minute
  });
}

/**
 * Hook to fetch the most recent execution for a connector
 *
 * Backend endpoint: GET /connectors/{connector_type}/{name}
 * Extracts the 'latest_execution' field from the connector response.
 *
 * Note: This does NOT call a dedicated /executions/latest endpoint
 * (which does not exist). Instead, it fetches the connector details
 * and extracts the latest_execution field.
 *
 * @param connectorType - The type of connector
 * @param name - The connector configuration name (required)
 * @param enabled - Whether to enable the query
 * @param refetchInterval - Optional auto-refetch interval in ms
 *
 * @example
 * // Auto-refresh every 30 seconds for running executions
 * const { data: latest } = useLatestExecution('google_ads', 'my-config', true, 30000);
 */
export function useLatestExecution(
  connectorType: string,
  name: string,
  enabled: boolean = true,
  refetchInterval?: number
) {
  return useQuery({
    queryKey: ['latestExecution', connectorType, name],
    queryFn: () => fetchLatestExecution(connectorType, name),
    enabled: enabled && !!connectorType && !!name,
    staleTime: 10000, // 10 seconds
    refetchInterval, // Optional auto-refresh
    refetchOnMount: 'always', // Always fetch fresh data when component mounts
  });
}
